import { Button, Icon } from '@anytag/shared-atoms';
import { useUserRoles } from '@anytag/auth';
import {
  delayRefetchedQuery,
  EngagementDetail,
  EngagementProposal,
  namedOperations,
  ProposalJobDescriptionBreakDownMode,
  useCreateEngagementInfluencerFoldersMutation,
  useGetCreateEngagementInfluencerFoldersAvailabilityQuery,
  useSnackbarHelper
} from '@anytag/graphql';
import { formatIntNumber, formatPercent, formatPrice, getSocialPairsIds } from '@anytag/shared-utils';
import { AddPackageWithAccounts } from '@anytag/shared-organisms';
import { ProposalDownloadButton } from '../shared';
import { ProposalDerivedProps } from '../shared/types';

interface ProposalSummaryProps
  extends Pick<ProposalDerivedProps, 'jdMode' | 'formattedInfluencers' | 'formattedSocialAccounts'> {
  engagement: EngagementDetail;
  hasTrackingReferral?: boolean;
  hasInfluencerCostPermission?: boolean;
  engagementProposal: EngagementProposal;
}

export const ProposalSummary = ({
  jdMode,
  engagement,
  hasTrackingReferral,
  formattedInfluencers,
  formattedSocialAccounts,
  hasInfluencerCostPermission = true,
  engagementProposal: { couponCodeCount, influencers }
}: ProposalSummaryProps) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const { isAgency, isAdminStaffTalentAgencyPartner, isAdminStaff } = useUserRoles();
  const { data, loading: isLoadingCreateEngagementInfluencerFoldersAvailability } =
    useGetCreateEngagementInfluencerFoldersAvailabilityQuery({
      skip: !isAdminStaff,
      variables: { campaignId: engagement.id }
    });
  const { callCreateEngagementInfluencerFolders, loading } = useCreateEngagementInfluencerFoldersMutation({
    refetchQueries: [namedOperations.Query.GetCreateEngagementInfluencerFoldersAvailability],
    onQueryUpdated: (observableQuery) => delayRefetchedQuery({ observableQuery, delay: 20_000 })
  });

  const influencerFoldersAvailability = data?.getCreateEngagementInfluencerFoldersAvailability;
  const isCreateEngagementInfluencerFoldersAvailable = influencerFoldersAvailability?.isAvailable;
  const campaignFolderId = influencerFoldersAvailability?.campaignFolderId;
  const isCampaignFolderExist = !!influencerFoldersAvailability?.isCampaignFolderExist;

  const couponCodes = couponCodeCount || 0;
  const currency = engagement?.currency || '';
  const numberOfInfluencers = engagement?.numberOfInfluencers ?? null;
  const selectedInfluencers = influencers.filter((infl) => !infl.isDenied);
  const numberOfSelectedInfluencers = selectedInfluencers.length;
  const {
    budget,
    followers,
    agencyCost,
    profit,
    engagementViewRate,
    engagementFollowerRate,
    engagementPostedCount,
    engagementProposedCount,
    influencerCost
  } = (() =>
    selectedInfluencers.reduce(
      (rs, { summary }) => ({
        budget: rs.budget + (summary.budget || 0),
        followers: rs.followers + (summary.followersCount || 0),
        agencyCost: rs.agencyCost + (summary.adAgencyMarginValue || 0),
        influencerCost: rs.influencerCost + (summary.influencerCost || 0),
        avgEngagement: rs.avgEngagement + (summary.averageEngagement || 0),
        engagementViewRate: rs.engagementViewRate + (summary.engagementViewRate || 0),
        engagementFollowerRate: rs.engagementFollowerRate + (summary.engagementFollowerRate || 0),
        engagementPostedCount: rs.engagementPostedCount + (summary.engagementPostedCount || 0),
        engagementProposedCount: rs.engagementProposedCount + (summary.engagementProposedCount || 0),
        profit: rs.profit + (summary.profit || 0)
      }),
      {
        budget: 0,
        followers: 0,
        agencyCost: 0,
        avgEngagement: 0,
        influencerCost: 0,
        profit: 0,
        engagementViewRate: 0,
        engagementPostedCount: 0,
        engagementFollowerRate: 0,
        engagementProposedCount: 0
      }
    ))();

  const stats = [
    {
      label: t('Suggested Influencers'),
      value:
        numberOfInfluencers !== null
          ? `${formatIntNumber(numberOfSelectedInfluencers)}/${formatIntNumber(numberOfInfluencers)}`
          : formatIntNumber(numberOfSelectedInfluencers)
    },
    ...(hasTrackingReferral
      ? [
          {
            label: (
              <div css={{ display: 'flex', alignItems: 'center' }}>
                {t('Coupon Codes')}
                {couponCodes < numberOfSelectedInfluencers ? (
                  <Icon icon="warning" variant="yellow" size="14px" className="ml-1.5" />
                ) : null}
              </div>
            ),
            value: formatIntNumber(couponCodes)
          }
        ]
      : []),
    { label: t('General.Total Followers'), value: formatIntNumber(followers) },
    {
      unit: '%',
      label: t('HeaderColumn.Agg Egg Rate by views'),
      value: formatPercent(engagementViewRate, false)
    },
    {
      unit: '%',
      label: t('HeaderColumn.Agg Egg Rate by followers'),
      value: formatPercent(engagementFollowerRate, false)
    },
    {
      label: t('JobDescription.Budget'),
      value: formatPrice(budget, currency),
      unit: `/${formatPrice(engagement?.budget, currency)} ${t(currency)}`
    },
    ...(isAdminStaffTalentAgencyPartner && hasInfluencerCostPermission
      ? [
          {
            label: t('JobDescription.Influencer Cost'),
            value: formatPrice(influencerCost, currency),
            unit: t(currency)
          },
          {
            unit: t(currency),
            label: t('JobDescription.Profit'),
            value: formatPrice(profit, currency)
          },
          ...(agencyCost
            ? [
                {
                  label: t('TextForm.Advertiser Agency Cost'),
                  value: formatPrice(agencyCost, currency),
                  unit: t(currency)
                }
              ]
            : [])
        ]
      : []),
    ...(isAgency && agencyCost
      ? [{ label: t('JobDescription.Profit'), value: formatPrice(agencyCost, currency), unit: t(currency) }]
      : []),
    ...(isAdminStaffTalentAgencyPartner
      ? [
          {
            label: t('HeaderColumn.Avg Engagement Proposed'),
            value: formatIntNumber(engagementProposedCount)
          },
          {
            label: t('HeaderColumn.Avg Engagement Posted'),
            value: formatIntNumber(engagementPostedCount)
          }
        ]
      : [])
  ];

  const { socialPairs } = getSocialPairsIds(formattedSocialAccounts);

  const handleCreateEngagementInfluencerFolders = async () => {
    try {
      await callCreateEngagementInfluencerFolders({ variables: { campaignId: engagement.id } });
      enqueueSnackbar(t("KOL's Google Drive folders have been created"), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <div className="bg-white">
      <div className="box-border flex items-center justify-between border-b-border px-4 py-2">
        <p className="text-lg leading-4 font-semibold text-primary">{t('Campaign Proposal')}</p>
        <div className="flex items-center gap-x-1">
          {isAdminStaff ? (
            campaignFolderId && !isCreateEngagementInfluencerFoldersAvailable ? (
              <Button asChild prefixIcon={{ icon: 'external-link', size: '16px' }}>
                <a
                  href={`https://drive.google.com/drive/folders/${campaignFolderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('Button.View KOL Google Folder')}
                </a>
              </Button>
            ) : (
              <Button
                prefixIcon={{ icon: 'external-link', size: '16px' }}
                title={t('Button.Create KOL Google Folder')}
                onClick={handleCreateEngagementInfluencerFolders}
                disabled={
                  !isCreateEngagementInfluencerFoldersAvailable ||
                  isLoadingCreateEngagementInfluencerFoldersAvailability
                }
                tooltip={{ disabled: isCampaignFolderExist, help: t('Tooltip.Campaign folder not exist') }}
                className="px-2"
                loading={{
                  showIcon: true,
                  status: loading
                }}
              />
            )
          ) : null}
          <AddPackageWithAccounts
            socialPairs={socialPairs}
            influencerIds={formattedInfluencers.map((infl) => infl.influencerId)}
            isInfluencerAccount={jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN}
          />
          <ProposalDownloadButton engagementId={engagement.id} disabled={numberOfSelectedInfluencers === 0} />
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] items-center gap-2 p-2">
        {stats.map(({ label, value, unit }, index) => (
          <div key={index} className="w-36 border-l-border p-2">
            <div className="overflow-hidden text-sm font-semibold text-ellipsis text-tertiary">{label}</div>
            <div
              className="mt-1 overflow-hidden text-lg font-semibold text-ellipsis whitespace-nowrap text-primary [&>span]:text-sm [&>span]:text-tertiary"
              title={`${value} ${unit || ''}`}
            >
              {value}
              &nbsp;
              {unit ? <span>{unit}</span> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
